import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { C } from '../data/constants.js';
import { AnimatedNumber } from './AnimatedNumber.js';
import { Sparkline } from './Sparkline.js';

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  delta?: number;
  deltaLabel?: string;
  sparkData?: Array<Record<string, unknown>>;
  sparkKey?: string;
  sparkColor?: string;
  delay?: number;
  compact?: boolean;
  loading?: boolean;
}

export function MetricCard({
  icon: Icon,
  label,
  value,
  prefix,
  suffix,
  decimals,
  delta,
  deltaLabel,
  sparkData,
  sparkKey,
  sparkColor,
  delay = 0,
  compact,
  loading,
}: MetricCardProps): React.ReactElement {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.cardBorder}`,
        borderRadius: 16,
        padding: compact ? '16px 18px' : '20px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 8 : 12,
        animation: `insights-fadeSlideUp 0.6s ease ${delay}ms both`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: C.accentDim,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={16} color={C.accent} />
          </div>
          <span style={{ fontSize: 12, color: C.textDim, fontWeight: 500, letterSpacing: 0.3 }}>
            {label}
          </span>
        </div>
        {delta !== undefined && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              padding: '3px 8px',
              borderRadius: 6,
              background: delta >= 0 ? 'rgba(0,207,160,0.1)' : C.redDim,
              fontSize: 11,
              fontWeight: 600,
              color: delta >= 0 ? C.accent : C.red,
            }}
          >
            {delta >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {delta >= 0 ? '+' : ''}
            {delta}
            {deltaLabel || ''}
          </div>
        )}
      </div>
      <div
        className={loading ? 'loading-blur' : undefined}
        style={{
          fontSize: compact ? 26 : 32,
          fontWeight: 700,
          color: '#fff',
          fontFamily: "'Space Mono', monospace",
          letterSpacing: -1,
        }}
      >
        {typeof value === 'string' ? (
          <span style={{ fontSize: compact ? 16 : 18, opacity: 0.45, letterSpacing: 0 }}>
            {value}
          </span>
        ) : (
          <AnimatedNumber
            value={value}
            prefix={prefix || ''}
            suffix={suffix || ''}
            decimals={decimals || 0}
          />
        )}
      </div>
      {sparkData && sparkKey && (
        <div
          style={{
            margin: `0 ${compact ? '-18px' : '-22px'} ${compact ? '-16px' : '-20px'}`,
            opacity: 0.7,
          }}
        >
          <Sparkline
            data={sparkData}
            dataKey={sparkKey}
            color={sparkColor || C.accent}
            height={50}
          />
        </div>
      )}
    </div>
  );
}
